/**
 * Iter 218 — Page d'édition d'un workflow.
 * Route : /admin/workflows/:workflowId
 *
 * Permet :
 *  - Éditer nom, description, trigger, actions (JSON)
 *  - Activer / désactiver
 *  - Run manuel (test) avec trigger_data factice
 *  - Voir les derniers runs
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, ArrowLeft, Save, Play, Trash2, AlertTriangle, CheckCircle2, Workflow, History, Plus, X, Code2, Layout, List } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import WorkflowCanvas from "../components/workflows/WorkflowCanvas";

const TRIGGER_TYPES = [
  { value: "lead.created", label: "📥 Nouveau lead créé" },
  { value: "lead.tag_added", label: "🏷️ Tag ajouté à un lead" },
  { value: "lead.score_threshold", label: "🎯 Seuil score IA franchi" },
  { value: "lead.tier_changed", label: "🔥 Tier changé (warm→hot, etc.)" },
  { value: "lead.converted", label: "🎉 Lead converti en client" },
  { value: "call.ended", label: "📞 Appel Aircall terminé" },
  { value: "call.transcribed", label: "🎙️ Appel transcrit (Whisper)" },
  { value: "form.submitted", label: "📝 Formulaire soumis" },
  { value: "stripe.payment_succeeded", label: "💰 Paiement Stripe réussi" },
  { value: "stripe.payment_failed", label: "💳 Paiement Stripe échoué" },
  { value: "meta.message_received", label: "💬 Message Meta reçu (WA/FB/IG)" },
  { value: "webhook.incoming", label: "🪝 Webhook entrant (Phase 2)" },
];

const ACTION_TYPES = [
  { value: "email.send", label: "✉️ Envoyer un email", fields: ["to", "subject", "body"] },
  { value: "sms.send", label: "💬 Envoyer un SMS", fields: ["to", "body"] },
  { value: "task.create", label: "📋 Créer une tâche", fields: ["title", "description", "priority", "user_id"] },
  { value: "tag.add", label: "🏷️ Ajouter un tag", fields: ["tags"] },
  { value: "tag.remove", label: "🏷️ Retirer un tag", fields: ["tags"] },
  { value: "prospect.update", label: "✏️ Mettre à jour un prospect", fields: ["fields"] },
  { value: "note.create", label: "📝 Ajouter une note", fields: ["text"] },
  { value: "notif.internal", label: "🔔 Notifier un utilisateur", fields: ["user_id", "title", "body", "url"] },
  { value: "webhook.outgoing", label: "🌐 Appeler un webhook", fields: ["url", "method", "body"] },
  { value: "ai.gemini_generate", label: "🤖 Générer texte IA Gemini", fields: ["prompt", "output_var", "max_tokens"] },
  { value: "onedrive.create_folder", label: "☁️ Créer dossier OneDrive", fields: ["folder_name", "parent_id"] },
  { value: "condition.if", label: "🔀 Condition (IF / ELSE)", fields: ["field", "op", "value", "on_true", "on_false"] },
  { value: "delay", label: "⏱️ Délai d'attente", fields: ["minutes"] },
];

const STATUS_BADGE = {
  active: { color: "#22c55e", bg: "bg-emerald-500/15", label: "Actif" },
  draft: { color: "#64748b", bg: "bg-slate-500/15", label: "Brouillon" },
};

export default function WorkflowEditorPage() {
  const { workflowId } = useParams();
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);

  const [wf, setWf] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [jsonInput, setJsonInput] = useState("");
  const [testData, setTestData] = useState('{"prospect_id":"prosp_test","name":"Test","email":"test@example.com","ai_evaluation":{"score":85,"tier":"hot"}}');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("blitzz_wf_view_mode") || "visual"); // 'visual' | 'list'
  const [selectedActionIdx, setSelectedActionIdx] = useState(null);

  useEffect(() => { localStorage.setItem("blitzz_wf_view_mode", viewMode); }, [viewMode]);

  const reload = async () => {
    setLoading(true);
    try {
      const [w, r] = await Promise.all([
        api.get(`/workflows/${workflowId}`),
        api.get(`/workflows/${workflowId}/runs?limit=10`),
      ]);
      setWf(w.data);
      setRuns(r.data?.runs || []);
      setJsonInput(JSON.stringify(w.data, null, 2));
    } catch {
      setWf(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (workflowId) reload(); /* eslint-disable-next-line */ }, [workflowId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workflows/${workflowId}`, {
        name: wf.name, description: wf.description, status: wf.status,
        trigger: wf.trigger, actions: wf.actions,
      });
      reload();
    } finally {
      setSaving(false);
    }
  };

  const saveFromJson = async () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setWf(parsed);
      await api.put(`/workflows/${workflowId}`, {
        name: parsed.name, description: parsed.description, status: parsed.status,
        trigger: parsed.trigger, actions: parsed.actions,
      });
      setShowJson(false);
      reload();
    } catch (e) {
      alert("JSON invalide : " + e.message);
    }
  };

  const runManual = async () => {
    setRunning(true);
    try {
      let data = {};
      try { data = JSON.parse(testData); } catch { /* noop */ }
      await api.post(`/workflows/${workflowId}/run`, { trigger_data: data });
      reload();
    } finally {
      setRunning(false);
    }
  };

  const removeWf = async () => {
    if (!window.confirm(`Supprimer "${wf.name}" et son historique ?`)) return;
    await api.delete(`/workflows/${workflowId}`);
    navigate("/admin/workflows");
  };

  const addAction = (type) => {
    const id = `a${(wf.actions || []).length + 1}_${Date.now().toString(36)}`;
    const newAction = { id, type, config: {} };
    setWf({ ...wf, actions: [...(wf.actions || []), newAction] });
  };

  const removeAction = (idx) => {
    const next = [...(wf.actions || [])];
    next.splice(idx, 1);
    setWf({ ...wf, actions: next });
  };

  const updateActionConfig = (idx, config) => {
    const next = [...(wf.actions || [])];
    next[idx] = { ...next[idx], config };
    setWf({ ...wf, actions: next });
  };

  if (loading) {
    return <div className="p-6 max-w-5xl mx-auto"><Loader2 className="w-5 h-5 animate-spin text-violet-400" /></div>;
  }
  if (!wf) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <Link to="/admin/workflows" className="text-xs text-violet-400 hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Retour
        </Link>
        <p className="mt-4 text-sm text-slate-400">Workflow introuvable.</p>
      </div>
    );
  }

  const sb = STATUS_BADGE[wf.status] || STATUS_BADGE.draft;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="workflow-editor-page">
      <Link to="/admin/workflows" className="text-xs text-violet-400 hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Retour à la liste
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white inline-flex items-center gap-2">
            <Workflow className="w-6 h-6 text-violet-400" />
            <Input value={wf.name} onChange={(e) => setWf({ ...wf, name: e.target.value })} className="bg-transparent border-0 text-2xl font-bold text-white p-0 h-auto focus-visible:ring-0" data-testid="wf-name" />
          </h1>
          <Textarea value={wf.description || ""} onChange={(e) => setWf({ ...wf, description: e.target.value })} placeholder="Description du workflow…" className="bg-transparent border-0 text-sm text-slate-400 p-0 mt-1 resize-none focus-visible:ring-0" rows={1} data-testid="wf-desc" />
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-1 rounded font-bold ${sb.bg}`} style={{ color: sb.color }}>{sb.label}</span>
          <Button onClick={() => setWf({ ...wf, status: wf.status === "active" ? "draft" : "active" })} variant="outline" size="sm" className="text-xs h-8" data-testid="wf-toggle-status">
            {wf.status === "active" ? "Désactiver" : "Activer"}
          </Button>
          <Button onClick={() => setShowJson(true)} variant="outline" size="sm" className="text-xs h-8" data-testid="wf-show-json">
            <Code2 className="w-3 h-3 mr-1" /> JSON
          </Button>
          <div className="flex items-center bg-[#14101c] border border-white/10 rounded h-8 overflow-hidden">
            <button
              onClick={() => setViewMode("visual")}
              className={`text-[11px] h-full px-2 inline-flex items-center gap-1 transition-colors ${viewMode === "visual" ? "bg-violet-500/20 text-violet-200" : "text-slate-400 hover:text-white"}`}
              data-testid="wf-view-visual"
              title="Vue visuelle (canvas)"
            >
              <Layout className="w-3 h-3" /> Visuel
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`text-[11px] h-full px-2 inline-flex items-center gap-1 transition-colors ${viewMode === "list" ? "bg-amber-500/20 text-amber-200" : "text-slate-400 hover:text-white"}`}
              data-testid="wf-view-list"
              title="Vue liste (édition détaillée)"
            >
              <List className="w-3 h-3" /> Liste
            </button>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] hover:bg-[#e6b820] text-[#14101c] text-xs h-8" data-testid="wf-save">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Sauvegarder
          </Button>
        </div>
      </div>

      {/* Trigger */}
      <div className="bg-[#0b0811] border border-violet-500/20 rounded-xl p-4" data-testid="wf-trigger">
        <h3 className="text-xs uppercase tracking-wider text-violet-400 font-bold mb-2">⚡ Déclencheur</h3>
        <select
          value={wf.trigger?.type || ""}
          onChange={(e) => setWf({ ...wf, trigger: { ...(wf.trigger || {}), type: e.target.value } })}
          className="w-full bg-[#14101c] border border-white/10 text-white text-xs rounded h-9 px-2"
          data-testid="trigger-type-select"
        >
          {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Visual canvas (react-flow) */}
      {viewMode === "visual" && (
        <WorkflowCanvas
          workflow={wf}
          onChange={setWf}
          onAddAction={(type) => addAction(type)}
          onSelectAction={(idx) => setSelectedActionIdx(idx)}
          selectedIndex={selectedActionIdx}
        />
      )}

      {/* Inline editor for selected action (visual mode) */}
      {viewMode === "visual" && selectedActionIdx !== null && selectedActionIdx >= 0 && wf.actions?.[selectedActionIdx] && (
        <div className="bg-[#0b0811] border border-amber-500/30 rounded-xl p-4" data-testid="wf-selected-editor">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wider text-amber-400 font-bold">
              Édition action #{selectedActionIdx + 1} — {(ACTION_TYPES.find((x) => x.value === wf.actions[selectedActionIdx].type) || {}).label}
            </h3>
            <div className="flex items-center gap-2">
              <button onClick={() => removeAction(selectedActionIdx) || setSelectedActionIdx(null)} className="text-rose-400 hover:text-rose-300 text-xs" data-testid="wf-selected-delete">
                <Trash2 className="w-3 h-3 inline mr-1" /> Supprimer
              </button>
              <button onClick={() => setSelectedActionIdx(null)} className="text-slate-500 hover:text-white">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <Textarea
            value={JSON.stringify(wf.actions[selectedActionIdx].config || {}, null, 2)}
            onChange={(e) => {
              try { updateActionConfig(selectedActionIdx, JSON.parse(e.target.value)); } catch { /* keep typing */ }
            }}
            className="bg-[#14101c] border-white/10 text-white text-xs font-mono"
            rows={Math.max(3, Math.min(15, JSON.stringify(wf.actions[selectedActionIdx].config || {}, null, 2).split("\n").length))}
            data-testid="wf-selected-config"
          />
          <p className="text-[10px] text-slate-500 mt-1">Variables disponibles : {`{{lead.name}}, {{lead.email}}, {{call.duration}}, etc.`}</p>
        </div>
      )}

      {/* Actions liste (mode liste classique) */}
      {viewMode === "list" && (
      <div className="space-y-2" data-testid="wf-actions">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs uppercase tracking-wider text-amber-400 font-bold">⚙️ Actions ({wf.actions?.length || 0})</h3>
        </div>
        {(wf.actions || []).map((a, idx) => {
          const meta = ACTION_TYPES.find((x) => x.value === a.type) || { label: a.type, fields: [] };
          return (
            <div key={a.id || idx} className="bg-[#0b0811] border border-white/10 rounded-xl p-4" data-testid={`action-${idx}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <span className="bg-amber-500/20 text-amber-300 text-[10px] font-bold px-1.5 py-0.5 rounded">{idx + 1}</span>
                  <span className="text-sm font-bold text-white">{meta.label}</span>
                </div>
                <button onClick={() => removeAction(idx)} className="text-rose-400 hover:text-rose-300 p-1" data-testid={`remove-action-${idx}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <Textarea
                value={JSON.stringify(a.config || {}, null, 2)}
                onChange={(e) => {
                  try { updateActionConfig(idx, JSON.parse(e.target.value)); } catch { /* keep typing */ }
                }}
                className="bg-[#14101c] border-white/10 text-white text-xs font-mono"
                rows={Math.max(3, Math.min(15, JSON.stringify(a.config || {}, null, 2).split("\n").length))}
                data-testid={`action-config-${idx}`}
              />
              <p className="text-[10px] text-slate-500 mt-1">Champs disponibles : {meta.fields.join(", ")} · Variables : {`{{lead.name}}, {{lead.email}}, {{call.duration}}, etc.`}</p>
            </div>
          );
        })}
        {/* Add action */}
        <div className="bg-[#0b0811] border border-dashed border-white/10 rounded-xl p-4">
          <div className="text-xs text-slate-400 mb-2">Ajouter une action :</div>
          <div className="flex flex-wrap gap-1.5">
            {ACTION_TYPES.map((t) => (
              <button key={t.value} onClick={() => addAction(t.value)} className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-amber-500/15 hover:text-amber-300 text-slate-300" data-testid={`add-action-${t.value}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Test runner */}
      <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4" data-testid="wf-test-runner">
        <h3 className="text-xs uppercase tracking-wider text-violet-400 font-bold mb-2 inline-flex items-center gap-1">
          <Play className="w-3 h-3" /> Tester en réel
        </h3>
        <p className="text-[11px] text-slate-400 mb-2">Exécute le workflow avec un payload de test (variables interpolées sur ces données) :</p>
        <Textarea value={testData} onChange={(e) => setTestData(e.target.value)} className="bg-[#0b0811] border-white/10 text-white text-xs font-mono" rows={3} data-testid="test-data" />
        <Button onClick={runManual} disabled={running} className="mt-2 bg-violet-500 hover:bg-violet-600 text-white text-xs h-8" data-testid="run-test">
          {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
          Lancer le test
        </Button>
      </div>

      {/* Runs history */}
      <div className="bg-[#0b0811] border border-white/5 rounded-xl p-4" data-testid="wf-runs">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-3 inline-flex items-center gap-1">
          <History className="w-3 h-3" /> Derniers runs
        </h3>
        {runs.length === 0 ? (
          <p className="text-[11px] text-slate-500 italic">Aucun run pour l'instant.</p>
        ) : (
          <div className="space-y-1">
            {runs.map((r) => (
              <div key={r.run_id} className="flex items-start gap-2 text-xs p-2 bg-[#14101c] rounded" data-testid={`run-${r.run_id}`}>
                {r.status === "success" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> :
                 r.status === "failed" ? <AlertTriangle className="w-3.5 h-3.5 text-rose-400 flex-shrink-0 mt-0.5" /> :
                 <Loader2 className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white font-mono text-[10px]">{r.run_id}</span>
                    <span className="text-[10px] text-slate-500">{r.created_at?.slice(0, 19).replace("T", " ")}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Status: <span className={r.status === "success" ? "text-emerald-300" : r.status === "failed" ? "text-rose-300" : "text-amber-300"}>{r.status}</span>
                    {" · "}{r.logs?.length || 0} actions exécutées
                    {r.error_message && <span className="text-rose-300 ml-2">· {r.error_message}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete */}
      <div className="border-t border-white/5 pt-4">
        <Button onClick={removeWf} variant="outline" className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10 text-xs" data-testid="wf-delete">
          <Trash2 className="w-3 h-3 mr-1" /> Supprimer le workflow
        </Button>
      </div>

      {/* JSON modal */}
      {showJson && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowJson(false)}>
          <div className="bg-[#0b0811] border border-white/10 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-sm font-bold text-white mb-3">Édition JSON brute</h2>
            <Textarea value={jsonInput} onChange={(e) => setJsonInput(e.target.value)} className="bg-[#14101c] border-white/10 text-white text-xs font-mono" rows={20} data-testid="json-edit" />
            <div className="flex justify-end gap-2 mt-3">
              <Button onClick={() => setShowJson(false)} variant="outline" size="sm" className="text-xs">Annuler</Button>
              <Button onClick={saveFromJson} className="bg-[#f7c935] text-[#14101c] text-xs" data-testid="json-save">Sauvegarder</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
